import React, { useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { M_Button, LoadingContext, NavigateContext } from 'src/components/M_Components'
import { CheckCircleOutlined, Description } from '@mui/icons-material'
import { BufferService } from 'src/components/M_Components/M_Utils/buffer.service'

export default function ECMRComponent({ orderId, orderService }) {
  const { t } = useTranslation()
  const [ecmrData, setEcmrData] = useState(null)
  const [ecmrValidation, setEcmrValidation] = useState(null)
  const [ecmrCapability, setEcmrCapability] = useState(null)
  const [loading, setLoading] = useState(false)
  const [xmlContent, setXmlContent] = useState('')
  const [cmrDocument, setCmrDocument] = useState(null)
  const ctx = useContext(LoadingContext)
  const ctx2 = useContext(NavigateContext)

  // Initialize BufferService instances
  const ecmrCapabilityService = new BufferService('ServiceOrder')
  const ecmrGenerateService = new BufferService('ServiceOrder')
  const ecmrXmlService = new BufferService('ServiceOrder')
  const ecmrUpdateService = new BufferService('ServiceOrder')
  const ecmrShowService = new BufferService('ServiceOrder')
  const cmrDocumentService = new BufferService('CmrDocument')

  useEffect(() => {
    if (orderService?.get('ServiceOrderId')) {
      checkEcmrCapability()
      loadCmrDocument()
    }
  }, [orderService])

  const checkEcmrCapability = async () => {
    if (!orderService?.get('ServiceOrderId')) return

    try {
      const data = await ecmrCapabilityService.getId(orderService.get('ServiceOrderId') + '/ecmr/capability')
      if (data && !data.response) {
        setEcmrCapability(data)
      } else {
        console.error('Error checking eCMR capability:', data)
        ctx.toastMessages('error', 'Error checking eCMR capability. Please try again.', '')
      }
    } catch (error) {
      console.error('Error checking eCMR capability:', error)
      ctx.toastMessages('error', 'Network error. Please check your connection and try again.', '')
    }
  }

  const loadCmrDocument = async () => {
    if (!orderService?.get('ServiceOrderId')) return

    try {
      const serviceOrderId = orderService.get('ServiceOrderId')
      const data = await cmrDocumentService.filter(`/list?serviceOrderId=${serviceOrderId}`)
      if (Array.isArray(data) && data.length > 0) {
        // Get the most recent CMR document (or first one if multiple)
        setCmrDocument(data[0])
      } else if (data && !data.response && !Array.isArray(data)) {
        // Single document returned
        setCmrDocument(data)
      } else {
        setCmrDocument(null)
      }
    } catch (error) {
      console.error('Error loading CMR document:', error)
      setCmrDocument(null)
    }
  }

  const handleViewCmrDocument = () => {
    if (cmrDocument) {
      ctx.passValue.set('cmrDocument', cmrDocument)
      ctx2.newTab('viewCmrDocument', `CMR ${cmrDocument.DocumentNumber || cmrDocument.CmrDocumentId}`)
    }
  }

  const generateEcmr = async () => {
    if (!orderService?.get('ServiceOrderId')) return

    setLoading(true)
    try {
      const data = await ecmrGenerateService.getId(orderService.get('ServiceOrderId') + '/ecmr/generate')
      if (data && !data.response) {
        // The API returns { EcmrData: {...}, Validation: {...} }, so we extract both
        setEcmrData(data.EcmrData || data)
        setEcmrValidation(data.Validation || null)
      } else {
        console.error('Error generating eCMR:', data)
      }
    } catch (error) {
      console.error('Error generating eCMR:', error)
    } finally {
      setLoading(false)
    }
  }

  const getEcmrXml = async () => {
    if (!orderService?.get('ServiceOrderId')) return

    try {
      const data = await ecmrXmlService.getId(orderService.get('ServiceOrderId') + '/ecmr/xml')
      if (data && !data.response) {
        setXmlContent(data)
      } else {
        console.error('Error getting eCMR XML:', data)
      }
    } catch (error) {
      console.error('Error getting eCMR XML:', error)
    }
  }

  const updateEcmr = async () => {
    if (!orderService?.get('ServiceOrderId')) return

    setLoading(true)
    try {
      const result = await ecmrUpdateService.postQuery(`/${orderService.get('ServiceOrderId')}/ecmr`)
      if (result && !result.response) {
        ctx.toastMessages('success', `eCMR ${ecmrCapability?.EcmrUuid ? 'updated' : 'uploaded'} successfully! UUID: ${result.Uuid}`, '')
        checkEcmrCapability() // Refresh capability status
      } else {
        // Handle error response from BufferService
        const error = result
        if (error.Message) {
          ctx.toastMessages('error', `Validation Error: ${error.Message || 'Failed to update eCMR'}`, '')
        } else {
          ctx.toastMessages('error', `Error: ${error.message || 'Failed to update eCMR'}`, '')
        }
      }
    } catch (error) {
      console.error('Error updating eCMR:', error)
      ctx.toastMessages('error', 'Error updating eCMR', '')
    } finally {
      setLoading(false)
    }
  }

  const showEcmr = async () => {
    if (!orderService?.get('ServiceOrderId')) return

    try {
      const result = await ecmrShowService.postQuery(`/${orderService.get('ServiceOrderId')}/showEcmr`)
      if (result && !result.response) {
        // Open eCMR in new window/tab
        window.open(`${process.env.REACT_APP_API_URL}/api/v1/ServiceOrder/${orderService.get('ServiceOrderId')}/showEcmr`, '_blank')
      } else {
        console.error('Error showing eCMR:', result)
      }
    } catch (error) {
      console.error('Error showing eCMR:', error)
    }
  }

  const renderCapabilityStatus = () => {
    if (!ecmrCapability) return <div>Loading...</div>

    return (
      <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h4 style={{ margin: 0 }}>eCMR Capability Status</h4>
          {cmrDocument && (
            <M_Button
              label={`View CMR Document ${cmrDocument.DocumentNumber || ''}`}
              icon={<Description />}
              onClick={handleViewCmrDocument}
              color="primary"
            />
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <CheckCircleOutlined style={{ color: ecmrCapability.HasCapability ? 'green' : 'red', marginRight: '10px' }} />
          <span>{ecmrCapability.HasCapability ? 'Service Order has eCMR capability' : 'Service Order missing required data for eCMR'}</span>
        </div>

        {cmrDocument && (
          <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
            <strong>Associated CMR Document:</strong> {cmrDocument.DocumentNumber || `CMR-${cmrDocument.CmrDocumentId}`}
            <br />
            <strong>Status:</strong> {cmrDocument.StateDisplayName || 'Unknown'}
            {cmrDocument.IsEcmrIntegrated && (
              <>
                <br />
                <strong>eCMR Integrated:</strong> Yes
                {cmrDocument.EcmrUuid && (
                  <>
                    <br />
                    <strong>eCMR UUID:</strong> {cmrDocument.EcmrUuid}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {ecmrCapability.EcmrUuid && (
          <div style={{ marginBottom: '10px' }}>
            <strong>eCMR UUID:</strong> {ecmrCapability.EcmrUuid}
          </div>
        )}

        {ecmrCapability.MissingData && ecmrCapability.MissingData.length > 0 && (
          <div>
            <strong>Missing Data:</strong>
            <ul>
              {ecmrCapability.MissingData.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  }

  const renderEcmrActions = () => {
    if (!ecmrCapability?.HasCapability) return null

    return (
      <div style={{ marginBottom: '20px' }}>
        <h4>eCMR Actions</h4>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <M_Button
            label="Generate eCMR Preview"
            onClick={generateEcmr}
            loading={loading}
            disabled={loading}
          />
          <M_Button
            label="Get eCMR XML"
            onClick={getEcmrXml}
            disabled={loading}
          />
          <M_Button
            label={ecmrCapability?.EcmrUuid ? "Update eCMR" : "Upload eCMR"}
            onClick={updateEcmr}
            loading={loading}
            disabled={loading}
            color="primary"
          />
          {ecmrCapability?.EcmrUuid && (
            <M_Button
              label="Show eCMR"
              onClick={showEcmr}
              disabled={loading}
              color="secondary"
            />
          )}
        </div>
      </div>
    )
  }

  const renderEcmrData = () => {
    if (!ecmrData) return null

    return (
      <div style={{ marginBottom: '20px' }}>
        <h4>eCMR Preview Data</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <h5>Document Information</h5>
            <p><strong>Document ID:</strong> {ecmrData.DocumentId || 'Not generated'}</p>
            <p><strong>Issue Date:</strong> {ecmrData.IssueDateTime ? new Date(ecmrData.IssueDateTime).toLocaleString() : 'Not set'}</p>
            <p><strong>Status:</strong> {ecmrData.DocumentStatus || 'Unknown'}</p>
          </div>

          <div>
            <h5>Parties</h5>
            {ecmrData.Consignor ? (
              <p><strong>Consignor:</strong> {ecmrData.Consignor.Name || 'Unknown'}</p>
            ) : (
              <p style={{ color: 'red' }}><strong>Consignor:</strong> Not available</p>
            )}
            {ecmrData.Consignee ? (
              <p><strong>Consignee:</strong> {ecmrData.Consignee.Name || 'Unknown'}</p>
            ) : (
              <p style={{ color: 'red' }}><strong>Consignee:</strong> Not available</p>
            )}
            {ecmrData.Carrier ? (
              <p><strong>Carrier:</strong> {ecmrData.Carrier.Name || 'Unknown'}</p>
            ) : (
              <p style={{ color: 'red' }}><strong>Carrier:</strong> Not available</p>
            )}
          </div>
        </div>

        {ecmrData.Goods && ecmrData.Goods.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h5>Goods Information</h5>
            {ecmrData.Goods.map((good, index) => (
              <div key={index} style={{ marginBottom: '10px', padding: '10px', border: '1px solid #eee', borderRadius: '4px' }}>
                <p><strong>Description:</strong> {good.Description || 'No description'}</p>
                <p><strong>Weight:</strong> {good.GrossWeight ? `${good.GrossWeight} ${good.WeightUnit || 'KGM'}` : 'Not specified'}</p>
                <p><strong>Volume:</strong> {good.GrossVolume ? `${good.GrossVolume} ${good.VolumeUnit || 'MTQ'}` : 'Not specified'}</p>
              </div>
            ))}
          </div>
        )}

        {ecmrData.Transport && (
          <div style={{ marginTop: '20px' }}>
            <h5>Transport Information</h5>
            <p><strong>Vehicle:</strong> {ecmrData.Transport.LicensePlate || 'Not specified'}</p>
            {ecmrData.Transport.Container && (
              <p><strong>Container:</strong> {ecmrData.Transport.Container.ContainerNumber || 'Not specified'}</p>
            )}
          </div>
        )}
      </div>
    )
  }

  const renderValidationResults = () => {
    if (!ecmrValidation) return null

    return (
      <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
        <h4>eCMR Validation Results</h4>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <CheckCircleOutlined
            style={{
              color: ecmrValidation.IsValid ? 'green' : 'red',
              marginRight: '10px'
            }}
          />
          <span>{ecmrValidation.IsValid ? 'eCMR data is valid' : 'eCMR data has validation errors'}</span>
        </div>

        {ecmrValidation.Errors && ecmrValidation.Errors.length > 0 && (
          <div style={{ marginBottom: '15px' }}>
            <h5 style={{ color: 'red' }}>Errors:</h5>
            <ul>
              {ecmrValidation.Errors.map((error, index) => (
                <li key={index} style={{ color: 'red' }}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {ecmrValidation.Warnings && ecmrValidation.Warnings.length > 0 && (
          <div>
            <h5 style={{ color: 'orange' }}>Warnings:</h5>
            <ul>
              {ecmrValidation.Warnings.map((warning, index) => (
                <li key={index} style={{ color: 'orange' }}>{warning}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  }

  const renderXmlContent = () => {
    if (!xmlContent) return null

    return (
      <div style={{ marginBottom: '20px' }}>
        <h4>eCMR XML Content</h4>
        <pre style={{
          backgroundColor: '#f5f5f5',
          padding: '15px',
          borderRadius: '5px',
          fontSize: '12px',
          overflow: 'auto',
          maxHeight: '400px'
        }}>
          {xmlContent}
        </pre>
      </div>
    )
  }

  return (
    <div style={{ padding: '20px' }}>
      <h3>eCMR - Electronic Consignment Manifest Report</h3>

      {renderCapabilityStatus()}
      {renderEcmrActions()}
      {renderEcmrData()}
      {renderValidationResults()}
      {renderXmlContent()}
    </div>
  )
}
